import React, { useMemo, useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Badge, Box, Button, Drawer, Empty, Input, Modal, NumberInput, Pagination, Select, goldAlert, goldToast } from "gold-ui-kit";
import AppShell from "../components/classic/AppShell";
import ClassicWindow from "../components/classic/ClassicWindow";
import Toolbar, { ToolbarButton, ToolbarSeparator } from "../components/classic/Toolbar";
import { useAuth } from "../context/AuthContext";
import { toLocalDateKey } from "../utils/date";
import {
  fetchProducts,
  fetchGroceryLists,
  saveGroceryList,
  fetchGroceryListItems,
  updateGroceryListItem,
  deleteGroceryListItem,
  addGroceryListItem,
  updateGroceryList,
} from "../store/slices/manav";
import { exportManagerPdfApi, printBranchPdfApi } from "../store/slices/manav/manavApi";

const UNITS = ["KG", "ADET", "DEMET", "PAKET", "KASA", "GR"];
const UNIT_OPTIONS = UNITS.map((u) => ({ value: u, label: u }));

const UNIT_TO_ENUM = {
  ADET: 1,
  KG: 2,
  GR: 3,
  KASA: 4,
  DEMET: 1,
  PAKET: 1,
};

const ENUM_TO_UNIT = {
  1: "ADET",
  2: "KG",
  3: "GR",
  4: "KASA",
};

const ZOOM_OPTIONS = [
  { value: 2, label: "200%" },
  { value: 1.75, label: "175%" },
  { value: 1.5, label: "150%" },
  { value: 1.25, label: "125%" },
  { value: 1, label: "100% (Normal)" },
  { value: 0.9, label: "90%" },
  { value: 0.85, label: "85%" },
  { value: 0.8, label: "80%" },
  { value: 0.75, label: "75% (Önerilen)" },
  { value: 0.7, label: "70%" },
  { value: 0.6, label: "60%" },
  { value: 0.5, label: "50%" },
];

const defaultTitle = () => `${new Date().toLocaleDateString("tr-TR")} Manav Listesi`;

const ProductRow = React.memo(({ product, idx, entry, updateEntry, handleKeyDown }) => {
  const defaultUnit = ENUM_TO_UNIT[product.unitType] || "KG";
  const e = entry || { unit: defaultUnit, quantity: 0 };
  return (
    <tr className={idx % 2 === 0 ? "bg-slate-50" : "bg-white"}>
      <td className="w-[76px] border border-slate-300 p-0.5">
        <Select
          size="xs"
          options={UNIT_OPTIONS}
          value={e.unit}
          tabIndex={-1}
          onChange={(ev) => updateEntry(product, "unit", ev.target.value)}
          data-testid={`manav-unit-${product.id}`}
        />
      </td>
      <td className="w-[80px] border border-slate-300 p-0.5">
        <Input
          size="xs"
          type="number"
          min="0"
          step="0.5"
          className="manav-qty-input !text-right"
          value={e.quantity || ""}
          onChange={(ev) => updateEntry(product, "quantity", ev.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={(ev) => ev.target.select()}
          data-testid={`manav-qty-${product.id}`}
        />
      </td>
      <td className="border border-slate-300 px-2 py-[1px] text-[10pt] font-medium">{product.name}</td>
    </tr>
  );
});

const ManavListPage = () => {
  const dispatch = useDispatch();
  const { catalog, savedLists } = useSelector((s) => s.manav);

  const { user } = useAuth();
  const [showHistory, setShowHistory] = useState(false);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [zoom, setZoom] = useState(1);

  const [title, setTitle] = useState(defaultTitle);
  const [date, setDate] = useState(() => toLocalDateKey());

  // productId -> { unit, quantity }
  const [entries, setEntries] = useState({});
  const [branchName, setBranchName] = useState("");
  const [activeListId, setActiveListId] = useState(null);
  const [originalItems, setOriginalItems] = useState([]);
  const [isExportingManager, setIsExportingManager] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportParams, setExportParams] = useState({
    padding: 4,
    fontSize: 10,
    fileName: "",
    listDate: "",
    perList: 38,
  });

  useEffect(() => {
    dispatch(fetchProducts());
    dispatch(fetchGroceryLists({ page: 1, size: 10 }));
  }, [dispatch]);

  // A4 pages of 60 items max (30 left, 30 right), sorted alphabetically
  const pages = useMemo(() => {
    const sorted = [...catalog].sort((a, b) => (a.name || "").localeCompare(b.name || "", "tr"));
    const chunks = [];
    const itemsPerPage = 60;
    for (let i = 0; i < sorted.length; i += itemsPerPage) {
      chunks.push(sorted.slice(i, i + itemsPerPage));
    }
    return chunks.length > 0 ? chunks : [[]];
  }, [catalog]);

  const updateEntry = (product, field, value) => {
    setEntries((prev) => {
      const defaultUnit = ENUM_TO_UNIT[product.unitType] || "KG";
      const existing = prev[product.id] || { unit: defaultUnit, quantity: 0 };
      return {
        ...prev,
        [product.id]: { ...existing, [field]: value },
      };
    });
  };

  const filledItems = useMemo(
    () =>
      Object.entries(entries)
        .filter(([, v]) => Number(v.quantity) > 0)
        .map(([productId, v]) => {
          const prod = catalog.find((p) => p.id === productId);
          return {
            productId,
            unitType: UNIT_TO_ENUM[v.unit] || prod?.unitType || 1,
            quantity: Number(v.quantity),
          };
        }),
    [entries, catalog]
  );

  const handleSave = () => {
    if (!branchName.trim()) {
      goldToast.warning("Şube adı (BranchName) girmek zorunludur.", { title: "Uyarı" });
      return;
    }
    if (filledItems.length === 0) {
      goldToast.warning("Listeye en az bir ürün miktarı girmelisiniz.", { title: "Uyarı" });
      return;
    }

    if (activeListId) {
      const deletions = [];
      const updates = [];
      const additions = [];

      originalItems.forEach((orig) => {
        const current = filledItems.find((it) => it.productId === orig.productId);
        if (!current) {
          deletions.push(orig.id);
        } else {
          if (current.quantity !== orig.quantity || current.unitType !== orig.unitType) {
            updates.push({
              id: orig.id,
              payload: {
                productId: orig.productId,
                groceryListId: activeListId,
                quantity: current.quantity,
                unitType: current.unitType
              }
            });
          }
        }
      });

      filledItems.forEach((current) => {
        const wasInOriginal = originalItems.some((orig) => orig.productId === current.productId);
        if (!wasInOriginal) {
          additions.push({
            productId: current.productId,
            groceryListId: activeListId,
            quantity: current.quantity,
            unitType: current.unitType
          });
        }
      });

      const originalList = savedLists.find((l) => l.id === activeListId);
      const listMetaChanged = originalList && (
        originalList.name !== title ||
        originalList.listDate !== date ||
        originalList.branchName !== branchName
      );

      const promises = [];

      deletions.forEach((id) => {
        promises.push(dispatch(deleteGroceryListItem(id)).unwrap());
      });

      updates.forEach((upd) => {
        promises.push(dispatch(updateGroceryListItem(upd)).unwrap());
      });

      additions.forEach((add) => {
        promises.push(dispatch(addGroceryListItem(add)).unwrap());
      });

      if (listMetaChanged) {
        promises.push(dispatch(updateGroceryList({
          id: activeListId,
          payload: {
            id: activeListId,
            name: title,
            listDate: date,
            branchName: branchName
          }
        })).unwrap());
      }

      Promise.all(promises)
        .then(() => {
          const descParts = [];
          if (additions.length > 0) descParts.push(`${additions.length} yeni ürün eklendi`);
          if (updates.length > 0) descParts.push(`${updates.length} ürün güncellendi`);
          if (deletions.length > 0) descParts.push(`${deletions.length} ürün silindi`);
          if (listMetaChanged) descParts.push("liste başlığı/detayları güncellendi");
          
          const description = descParts.length > 0 
            ? descParts.join(", ") + " başarıyla kaydedildi."
            : "Herhangi bir değişiklik yapılmadı.";

          goldToast.success(description, { title: "Liste Güncellendi" });
          setEntries({});
          setBranchName("");
          setActiveListId(null);
          setOriginalItems([]);
          dispatch(fetchGroceryLists({ page: 1, size: 10 }));
        })
        .catch((err) => {
          goldToast.error("Değişiklikler kaydedilirken hata oluştu: " + err, { title: "Hata" });
        });

    } else {
      dispatch(saveGroceryList({ name: title, listDate: date, branchName: branchName, items: filledItems }))
        .unwrap()
        .then(() => {
          goldToast.success("Manav listesi başarıyla kaydedildi.", { title: "Başarılı" });
          setEntries({});
          setBranchName("");
          dispatch(fetchGroceryLists({ page: 1, size: 10 }));
        })
        .catch((err) => {
          goldToast.error("Kaydetme sırasında hata oluştu: " + err, { title: "Hata" });
        });
    }
  };

  const handleClear = async () => {
    const result = await goldAlert.fire({
      title: "Tüm girişler temizlensin mi?",
      text: "Listeye girdiğiniz tüm miktarlar silinecek.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Evet, Temizle",
      cancelButtonText: "Vazgeç",
      confirmButtonColor: "danger",
      cancelButtonColor: "default",
    });
    if (!result.isConfirmed) return;
    setEntries({});
    setTitle(defaultTitle());
    setDate(toLocalDateKey());
    setBranchName("");
    setActiveListId(null);
    setOriginalItems([]);
  };

  const handlePrint = () => {
    if (filledItems.length === 0) {
      goldToast.warning("Yazdırmak için listeye en az bir ürün miktarı girmelisiniz.", { title: "Uyarı" });
      return;
    }
    window.print();
  };

  const handleExportManager = () => {
    setExportParams({
      padding: 4,
      fontSize: 10,
      fileName: title || "Manav_Listesi_Yonetici",
      listDate: date,
      perList: 38
    });
    setShowExportModal(true);
  };

  const handleExportManagerConfirm = async () => {
    setShowExportModal(false);
    setIsExportingManager(true);
    try {
      const blobData = await exportManagerPdfApi({
        Padding: exportParams.padding,
        FontSize: exportParams.fontSize,
        FileName: exportParams.fileName || "Manav_Listesi_Yonetici",
        ListDate: exportParams.listDate,
        PerList: exportParams.perList
      });
      
      const downloadUrl = window.URL.createObjectURL(new Blob([blobData], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.setAttribute("download", `${exportParams.fileName || "Manav_Listesi_Yonetici"}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      
      goldToast.success("Yönetici listesi PDF olarak indirildi.", { title: "Başarılı" });
    } catch (err) {
      goldToast.error("Yönetici listesi dışa aktarılamadı: " + (err.message || err), { title: "Hata" });
    } finally {
      setIsExportingManager(false);
    }
  };

  const handlePrintBranch = async (listId, listName, listDate) => {
    try {
      const blobData = await printBranchPdfApi({
        Id: listId,
        FileName: listName || `${listDate}_Inceleme`,
        Padding: 4,
        FontSize: 10
      });
      
      const downloadUrl = window.URL.createObjectURL(new Blob([blobData], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.setAttribute("download", `${listName || `${listDate}_Inceleme`}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      
      goldToast.success("Şube listesi PDF olarak indirildi.", { title: "Başarılı" });
    } catch (err) {
      goldToast.error("Şube listesi indirilemedi: " + (err.message || err), { title: "Hata" });
    }
  };

  const handleKeyDown = (ev) => {
    if (ev.key === "Enter" || ev.key === "ArrowDown" || (ev.key === "Tab" && !ev.shiftKey)) {
      ev.preventDefault();
      const inputs = Array.from(document.querySelectorAll(".manav-qty-input"));
      const currentIndex = inputs.indexOf(ev.target);
      if (currentIndex !== -1 && currentIndex < inputs.length - 1) {
        inputs[currentIndex + 1].focus();
      }
    } else if (ev.key === "ArrowUp" || (ev.key === "Tab" && ev.shiftKey)) {
      ev.preventDefault();
      const inputs = Array.from(document.querySelectorAll(".manav-qty-input"));
      const currentIndex = inputs.indexOf(ev.target);
      if (currentIndex > 0) {
        inputs[currentIndex - 1].focus();
      }
    }
  };

  const setExportParam = (key, value) => setExportParams((prev) => ({ ...prev, [key]: value }));

  const totalQuantity = filledItems.reduce((s, it) => s + Number(it.quantity || 0), 0).toLocaleString("tr-TR");

  return (
    <AppShell pageId="manav">
      <ClassicWindow title="Manav Listesi Girişi" width="100%">
        <div className={isFullScreen ? "fixed inset-0 z-[995] flex flex-col overflow-auto bg-slate-700 p-4" : "flex w-full flex-col"}>
          <Toolbar>
            <ToolbarButton icon="💾" label="Kaydet" onClick={handleSave} testId="manav-save-btn" />
            <ToolbarButton
              icon={isExportingManager ? "⌛" : "📤"}
              label={isExportingManager ? "Aktarılıyor..." : "Yönetici PDF Aktar"}
              onClick={handleExportManager}
              disabled={isExportingManager}
              testId="manav-export-manager-btn"
            />
            <ToolbarButton icon="🧹" label="Temizle" onClick={handleClear} testId="manav-clear-btn" />
            <ToolbarButton icon="⏳" label="Geçmiş Listeler" onClick={() => setShowHistory(true)} testId="manav-history-btn" />
            <ToolbarButton
              icon={isFullScreen ? "📴" : "🖥️"}
              label={isFullScreen ? "Normal Ekran" : "Tam Ekran"}
              active={isFullScreen}
              onClick={() => {
                setIsFullScreen(!isFullScreen);
                setZoom(isFullScreen ? 1 : 0.75);
              }}
              testId="manav-fullscreen-btn"
            />
            <ToolbarSeparator />
            <div className="flex items-center gap-2 px-1 text-xs font-semibold text-slate-700">
              🔍 Ölçek
              <div className="w-36">
                <Select size="xs" options={ZOOM_OPTIONS} value={zoom} onChange={(e) => setZoom(Number(e.target.value))} />
              </div>
            </div>
            <ToolbarSeparator />
            <Badge size="sm" radius="full" variant="soft" color={filledItems.length > 0 ? "success" : "default"}>
              Doldurulan: {filledItems.length} / {catalog.length} ürün
            </Badge>
            {activeListId && (
              <Badge size="sm" radius="full" variant="soft" color="warning">
                Kayıtlı liste düzenleniyor
              </Badge>
            )}
          </Toolbar>

          <div className="flex min-h-[80vh] w-full flex-col items-center gap-6 overflow-auto bg-slate-600 p-4">
            {pages.map((pageProducts, pIdx) => {
              const leftProducts = pageProducts.slice(0, 30);
              const rightProducts = pageProducts.slice(30, 60);
              return (
                <div
                  key={pIdx}
                  className="a4-page manav-a4 origin-top shadow-xl transition-all duration-300"
                  data-testid={`manav-a4-paper-${pIdx}`}
                  style={{
                    width: "210mm",
                    minHeight: "297mm",
                    transform: `scale(${zoom})`,
                    marginBottom: `${(zoom - 1) * 297}mm`,
                    padding: "8mm",
                  }}
                >
                  <div className="mb-3 flex items-end justify-between gap-4 border-b-2 border-black pb-2">
                    <div className="w-[90mm]">
                      <div className="mb-1 text-[8pt] text-slate-500">Şube Yönetim Sistemi</div>
                      <Input
                        size="sm"
                        className="!text-[13pt] !font-bold"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        data-testid="manav-title-input"
                      />
                    </div>
                    <div className="flex shrink-0 items-end gap-3 text-[10pt]">
                      <div className="w-[42mm]">
                        <div className="mb-1 text-[8pt] text-slate-500">
                          Şube <span className="font-bold text-red-500">*</span>
                        </div>
                        <Input
                          size="sm"
                          value={branchName}
                          placeholder="Şube Adı Girin"
                          isValid={branchName.trim().length > 0 || filledItems.length === 0}
                          onChange={(e) => setBranchName(e.target.value)}
                          data-testid="manav-branchname-input"
                        />
                      </div>
                      <div className="w-[40mm]">
                        <div className="mb-1 text-[8pt] text-slate-500">Tarih</div>
                        <Input size="sm" type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="manav-date-input" />
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4" style={{ minHeight: "240mm" }}>
                    {[leftProducts, rightProducts].map((products, ci) => (
                      <table key={ci} className="w-full self-start border-collapse" data-testid={`manav-column-${pIdx}-${ci}`}>
                        <thead>
                          <tr className="bg-slate-200">
                            <th className="w-[76px] border border-slate-300 px-1 py-1 text-[9pt] font-bold">Birim</th>
                            <th className="w-[80px] border border-slate-300 px-1 py-1 text-[9pt] font-bold">Miktar</th>
                            <th className="border border-slate-300 px-2 py-1 text-left text-[9pt] font-bold">Ürün</th>
                          </tr>
                        </thead>
                        <tbody>
                          {products.map((p, idx) => (
                            <ProductRow
                              key={p.id}
                              product={p}
                              idx={idx}
                              entry={entries[p.id]}
                              updateEntry={updateEntry}
                              handleKeyDown={handleKeyDown}
                            />
                          ))}
                        </tbody>
                      </table>
                    ))}
                  </div>

                  <div className="mt-4 flex items-center justify-between border-t border-dashed border-black pt-3 text-[9pt]">
                    <div>
                      {pIdx === pages.length - 1 ? (
                        <>
                          <b>Toplam Ürün Çeşidi:</b> {filledItems.length}
                        </>
                      ) : (
                        <span className="italic text-slate-500">Devamı Sayfa {pIdx + 2}'de...</span>
                      )}
                    </div>
                    <div className="font-bold text-slate-500">
                      Sayfa {pIdx + 1} / {pages.length}
                    </div>
                    <div>
                      {pIdx === pages.length - 1 ? (
                        <>
                          <b>Toplam Miktar:</b> {totalQuantity}
                        </>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </div>
                    <div>
                      <b>Hazırlayan:</b>{" "}
                      <span className="border-b border-black px-2 font-semibold">{user?.fullName || user?.username || "___________________"}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </ClassicWindow>

      <ManavHistoryDrawer
        isOpen={showHistory}
        onClose={() => setShowHistory(false)}
        setEntries={setEntries}
        setTitle={setTitle}
        setDate={setDate}
        setBranchName={setBranchName}
        onPrintBranch={handlePrintBranch}
        setActiveListId={setActiveListId}
        setOriginalItems={setOriginalItems}
      />

      <Modal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        width={460}
        className="max-w-[95vw]"
        title="Yönetici PDF Dışa Aktar Parametreleri"
        footer={
          <>
            <Button variant="outline" color="default" onClick={() => setShowExportModal(false)}>
              İptal
            </Button>
            <Button onClick={handleExportManagerConfirm}>📥 Aktar</Button>
          </>
        }
      >
        <div className="flex flex-col gap-4 text-sm">
          <div className="flex flex-col gap-1.5">
            <span className="text-xs font-semibold text-slate-700">Dosya Adı</span>
            <Input value={exportParams.fileName} onChange={(e) => setExportParam("fileName", e.target.value)} />
          </div>
          <div className="flex flex-col gap-1.5">
            <span className="text-xs font-semibold text-slate-700">Liste Tarihi</span>
            <Input type="date" value={exportParams.listDate} onChange={(e) => setExportParam("listDate", e.target.value)} />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="flex flex-col gap-1.5">
              <span className="text-xs font-semibold text-slate-700">Padding</span>
              <NumberInput min={0} max={20} value={exportParams.padding} onChange={(v) => setExportParam("padding", v ?? 0)} />
            </div>
            <div className="flex flex-col gap-1.5">
              <span className="text-xs font-semibold text-slate-700">Font Boyutu</span>
              <NumberInput min={6} max={24} value={exportParams.fontSize} onChange={(v) => setExportParam("fontSize", v ?? 10)} />
            </div>
            <div className="flex flex-col gap-1.5">
              <span className="text-xs font-semibold text-slate-700">Sayfa Başına</span>
              <NumberInput min={10} max={100} value={exportParams.perList} onChange={(v) => setExportParam("perList", v ?? 38)} />
            </div>
          </div>
        </div>
      </Modal>
    </AppShell>
  );
};

const ManavHistoryDrawer = ({ isOpen, onClose, setEntries, setTitle, setDate, setBranchName, onPrintBranch, setActiveListId, setOriginalItems }) => {
  const dispatch = useDispatch();
  const { savedLists, page, count, loading, catalog } = useSelector((s) => s.manav);
  const [loadingListId, setLoadingListId] = useState(null);
  const [loadingPdfId, setLoadingPdfId] = useState(null);

  useEffect(() => {
    if (isOpen) dispatch(fetchGroceryLists({ page: 1, size: 10 }));
  }, [dispatch, isOpen]);

  const busy = loadingListId !== null || loadingPdfId !== null;

  const loadList = (l) => {
    setLoadingListId(l.id);
    dispatch(fetchGroceryListItems(l.id))
      .unwrap()
      .then((items) => {
        const newEntries = {};
        (items || []).forEach((it) => {
          const prod = catalog.find((p) => p.id === it.productId);
          if (prod) {
            newEntries[prod.id] = {
              unit: ENUM_TO_UNIT[it.unitType] || ENUM_TO_UNIT[prod.unitType] || "KG",
              quantity: it.quantity,
            };
          }
        });
        setEntries(newEntries);
        setTitle(l.name || `${l.listDate} Manav Listesi`);
        setDate(l.listDate);
        setBranchName(l.branchName || "");
        setActiveListId(l.id);
        setOriginalItems(items || []);
        onClose();
      })
      .catch((err) => {
        goldToast.error("Liste detayları alınamadı: " + err, { title: "Hata" });
      })
      .finally(() => setLoadingListId(null));
  };

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      position="right"
      size={95}
      variant="blur"
      className="!w-[440px] !max-w-[95vw]"
      title={
        <span className="flex items-center gap-2 text-sm font-bold">
          📚 Geçmiş Manav Listeleri
          <Badge size="xs" radius="full" variant="soft" color="primary">
            {count} Liste
          </Badge>
        </span>
      }
      footer={
        <Button variant="outline" color="default" className="!w-full" onClick={onClose}>
          Kapat
        </Button>
      }
    >
      <div className="flex flex-col gap-2">
        {savedLists.length === 0 ? (
          <Empty size="sm" title={loading ? "Yükleniyor..." : "Kayıtlı liste yok"} description="Henüz kayıtlı liste bulunmamaktadır." />
        ) : (
          savedLists.map((l) => (
            <Box key={l.id} className="flex items-center justify-between gap-2 !overflow-visible !rounded-xl !border-slate-200 !px-3 !py-2.5 !shadow-sm">
              <div className="min-w-0 flex-1">
                <div className="truncate text-[12px] font-bold text-slate-800">{l.name || `${l.listDate} Listesi`}</div>
                <div className="mt-0.5 flex flex-wrap gap-x-2 text-[10px] text-slate-500">
                  <span>📅 {l.listDate}</span>
                  {l.branchName && <span className="font-medium text-blue-600">🏢 {l.branchName}</span>}
                </div>
              </div>
              <div className="flex shrink-0 gap-1.5">
                <Button
                  size="xs"
                  variant="filled"
                  color="warning"
                  disabled={busy}
                  loading={loadingPdfId === l.id}
                  onClick={() => {
                    setLoadingPdfId(l.id);
                    onPrintBranch(l.id, l.name, l.listDate).finally(() => setLoadingPdfId(null));
                  }}
                >
                  📄 PDF
                </Button>
                <Button size="xs" disabled={busy} loading={loadingListId === l.id} onClick={() => loadList(l)}>
                  Yükle
                </Button>
              </div>
            </Box>
          ))
        )}

        {count > 10 && (
          <div className="mt-3 flex justify-center border-t border-slate-100 pt-3">
            <Pagination
              current={page}
              perPage={10}
              totalItems={count}
              prevButtonText="Önceki"
              nextButtonText="Sonraki"
              buttonProps={{ size: "xs", disabled: loading }}
              onChangePage={(p) => dispatch(fetchGroceryLists({ page: p, size: 10 }))}
            />
          </div>
        )}
      </div>
    </Drawer>
  );
};

export default ManavListPage;
